'use client'

import { useEffect, useState } from 'react'
import { bookingConfig } from '@cocoonspace/shared/config/booking-config'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Plural, useLingui } from '@lingui/react/macro'
import {
	CheckSquareIcon,
	ChevronDownIcon,
	SquareIcon,
	UsersIcon,
	XIcon,
} from 'lucide-react'

import {
	DrawerPopover,
	DrawerPopoverContent,
	DrawerPopoverTrigger,
} from '~/components/drawer-dialog'

const getDefaultCapacityState = (value?: string[]) =>
	bookingConfig.capacities.reduce(
		(acc, option) => {
			acc[option.value] = value?.includes(option.value) || false

			return acc
		},
		{} as Record<string, boolean>,
	)

export const SearchCapacityFilter = ({
	onChange,
	value,
	className,
}: {
	onChange: (capacities?: string[]) => void
	value: string[] | undefined
	className?: string
}) => {
	const { t } = useLingui()
	const [isOpen, setIsOpen] = useState(false)

	const [capacities, setCapacities] = useState<Record<string, boolean>>(() =>
		getDefaultCapacityState(value),
	)

	// biome-ignore lint/correctness/useExhaustiveDependencies: sync local state when popover opens or value changes
	useEffect(() => {
		setCapacities(getDefaultCapacityState(value))
	}, [isOpen, value])

	const handleConfirm = () => {
		const checkedCapacities = Object.entries(capacities)
			.filter(([, isChecked]) => isChecked)
			.map(([capacityValue]) => capacityValue)

		onChange(checkedCapacities.length ? checkedCapacities : undefined)
	}

	return (
		<DrawerPopover
			onOpenChange={setIsOpen}
			open={isOpen}
		>
			<Card
				className={cn(
					'flex h-[32px] flex-row items-center rounded text-sm',
					className,
				)}
			>
				<DrawerPopoverTrigger
					className='flex flex-1 flex-row items-center gap-2 p-2'
					data-testid='floating-input'
				>
					<UsersIcon
						className='text-primary'
						size={14}
					/>

					{value?.length === 1 ? (
						<span className='text-sm'>{value[0]}</span>
					) : (
						<Plural
							value={value?.length ?? 0}
							_0='Capacité'
							one='# Capacité'
							other='# Capacités'
						/>
					)}

					{!value?.length && (
						<ChevronDownIcon
							className='shrink-0'
							size={16}
							strokeWidth={1}
						/>
					)}
				</DrawerPopoverTrigger>

				{!!value?.length && (
					<button
						type='button'
						className='pr-2'
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							onChange(undefined)
							setIsOpen(false)
						}}
						aria-label={t`Effacer le filtre capacité`}
					>
						<XIcon
							className='text-primary'
							size={16}
						/>
					</button>
				)}
			</Card>

			<DrawerPopoverContent
				className='flex flex-col divide-y overflow-hidden p-0'
				onOpenAutoFocus={(e) => e.preventDefault()}
				sideOffset={10}
			>
				<div className='flex flex-1 flex-col gap-2 overflow-y-auto p-4'>
					{bookingConfig.capacities.map((option, i) => (
						<button
							key={option.value}
							type='button'
							className='flex w-full items-center gap-2 truncate rounded-md py-1 text-left text-sm'
							data-testid={`capacity-option-${i}`}
							onClick={() => {
								setCapacities((prev) => ({
									...prev,
									[option.value]: !prev[option.value],
								}))
							}}
						>
							{capacities[option.value] ? (
								<CheckSquareIcon className='text-primary size-4' />
							) : (
								<SquareIcon className='text-primary size-4' />
							)}

							<span className='text-sm'>{option.label}</span>
						</button>
					))}
				</div>

				<div className='flex flex-row justify-end gap-6 p-4'>
					<Button
						data-testid='validate-capacity-btn'
						onClick={() => {
							handleConfirm()
							setIsOpen(false)
						}}
						size='sm'
						variant='outline'
					>
						{t`Valider`}
					</Button>
				</div>
			</DrawerPopoverContent>
		</DrawerPopover>
	)
}
